import React from "react";
import Select from "react-select";
import { sweetalert } from "../../App";
import { isBlank, isOnlyWhitespace } from "../../util/ValidationUtil";
import { buildRow, getBoolSelectOptions, getBoolSelectVal } from "../../util/FormatUtil";
import { isEmptyObject } from "jquery";
import { FAQ } from "../../types/FAQ";
import { Category } from "../../types/Category";
import FAQAPI from "../../network/FAQAPI";

interface FAQManagementModalState {
  selectedFAQ: FAQ;
  // A category created from this modal, held until the parent's reload brings it back
  pendingCategory: { label: string, value: number } | null;
}
interface FAQManagementModalProps {
  selectedFAQ: FAQ;
  categories: Category[];
  reloadCategories?: () => void;
  onSubmit?;
}

export class FAQManagementModal extends React.Component<FAQManagementModalProps, FAQManagementModalState> {
  public static ID = "faqmanagement";

  constructor(props) {
    super(props);
    this.state = {
      selectedFAQ: {
        ID: null,
        Question: "",
        Answer: "",
        CategoryID: null,
        IsActive: 1,
      },
      pendingCategory: null,
    };
    this.handleAddCategory = this.handleAddCategory.bind(this);
  }

  componentDidMount() {
    // Seed the form when the modal opens rather than on every prop change: adding a
    // category reloads the parent's category list, and reseeding on that re-render
    // would wipe whatever has been typed into Question/Answer.
    window["$"](`#${FAQManagementModal.ID}`).on('show.bs.modal', () => this.seedFromProps());
    // Release Bootstrap's focus trap so inputs in nested dialogs (e.g. the
    // SweetAlert "Add New Category" prompt) accept typing. Use the global
    // jQuery instance that Bootstrap dispatches modal events on.
    window["$"](`#${FAQManagementModal.ID}`).on('shown.bs.modal', function () {
      window["$"](document).off('focusin.modal');
    });
  }

  seedFromProps() {
    let faq = this.props.selectedFAQ;
    let hasFAQ = !!faq && !isEmptyObject(faq);
    this.setState({
      selectedFAQ: {
        ID: hasFAQ && faq.ID ? faq.ID : null,
        Question: hasFAQ && faq.Question ? faq.Question : "",
        Answer: hasFAQ && faq.Answer ? faq.Answer : "",
        CategoryID: hasFAQ && faq.CategoryID ? faq.CategoryID : null,
        CategoryName: hasFAQ && faq.CategoryName ? faq.CategoryName : "",
        IsActive: hasFAQ && (faq.IsActive || faq.IsActive === 0) ? faq.IsActive : 1,
      },
      pendingCategory: null,
    });
  }

  getCategoryOptions() {
    let options = (this.props.categories || []).map(c => ({ label: c.Name, value: c.ID }));
    // A category just created here is selectable before the parent's reload lands
    let pending = this.state.pendingCategory;
    if (pending && !options.find(o => o.value === pending.value)) {
      options.push({ label: pending.label, value: pending.value });
    }
    // Preserve the FAQ's current category even if it has since been disabled
    // (disabled categories are not in the active list), so saving won't clear it.
    let current = this.state.selectedFAQ.CategoryID;
    if (current && !options.find(o => o.value === current)) {
      options.push({
        label: `${this.state.selectedFAQ.CategoryName || "Current category"} (inactive)`,
        value: current,
      });
    }
    return options;
  }

  getSelectedCategoryOption() {
    if (!this.state.selectedFAQ.CategoryID) return null;
    return this.getCategoryOptions().find(o => o.value === this.state.selectedFAQ.CategoryID) || null;
  }

  async handleAddCategory() {
    const { value: name } = await sweetalert.fire({
      title: "Add New Category",
      input: "text",
      inputLabel: "Category Name",
      inputPlaceholder: "Enter a category name",
      showCancelButton: true,
      confirmButtonText: "Add",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "Category name cannot be empty";
        }
      },
    });

    if (!name) return;

    let response = await FAQAPI.createCategory({ Name: name.trim(), IsActive: 1 });
    if (!response.success) {
      return sweetalert.fire({ icon: "error", title: "", text: response.reason ? response.reason : "Unable to add category." });
    }

    let newID = response.data && response.data.ID ? response.data.ID : null;
    let categoryName = name.trim();
    // Select the new category immediately; the parent's reload replaces the
    // pending entry with the real one when it lands.
    if (newID) {
      this.setState((prevState) => ({
        selectedFAQ: { ...prevState.selectedFAQ, CategoryID: newID, CategoryName: categoryName },
        pendingCategory: { label: categoryName, value: newID },
      }));
    }
    if (this.props.reloadCategories) {
      this.props.reloadCategories();
    }
  }

  render() {
    return (
      <div className="modal fade form_modal" id={FAQManagementModal.ID} tabIndex={-1} role="dialog"
        aria-labelledby="faq_modal_label"
        aria-hidden="true">
        <div className="modal-dialog modal-lg modal-xl" role="document">
          <div className="modal-content">
            <div className="container-fluid">
              <div className="modal-header">
                <h5 className="modal-title" id="faq_modal_label">FAQ Management</h5>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {buildRow("Question",
                  <input className={"form-control"}
                    maxLength={500}
                    autoComplete={"off"}
                    type={"search"}
                    name={"Question"}
                    onChange={(e) => {
                      this.setState((prevState) => ({
                        selectedFAQ: { ...prevState.selectedFAQ, Question: e.target.value }
                      }))
                    }}
                    value={this.state.selectedFAQ.Question}
                  />,
                  'The question shown to users',
                  true
                )}
                {buildRow("Answer",
                  <textarea className={"form-control"}
                    autoComplete={"off"}
                    name={"Answer"}
                    rows={5}
                    onChange={(e) => {
                      this.setState((prevState) => ({
                        selectedFAQ: { ...prevState.selectedFAQ, Answer: e.target.value }
                      }))
                    }}
                    value={this.state.selectedFAQ.Answer}
                  />,
                  'The answer shown to users',
                  true
                )}
                {buildRow("Category",
                  <div className="d-flex align-items-center">
                    <div style={{ flex: 1 }}>
                      <Select
                        isSearchable={true}
                        isClearable={true}
                        placeholder={"Please Select..."}
                        noOptionsMessage={() => "No categories"}
                        value={this.getSelectedCategoryOption()}
                        onChange={(e) => this.setState((prevState) => ({
                          selectedFAQ: { ...prevState.selectedFAQ, CategoryID: e ? e.value : null }
                        }))}
                        className={"state_select"}
                        options={this.getCategoryOptions()}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-success ml-2"
                      onClick={this.handleAddCategory}
                    >
                      Add New
                    </button>
                  </div>,
                  'Optional grouping for the FAQ. Use "Add New" to create a category.'
                )}
                {buildRow("Is Active?",
                  <Select
                    isSearchable={true}
                    placeholder={"Please Select..."}
                    noOptionsMessage={() => "No option"}
                    value={getBoolSelectVal(this.state.selectedFAQ.IsActive)}
                    onChange={(e) => this.setState((prevState) => ({
                      selectedFAQ: { ...prevState.selectedFAQ, IsActive: e.value ? 1 : 0 }
                    }))}
                    className={"state_select"}
                    options={getBoolSelectOptions()}
                  />,
                  'Only active FAQs are shown to users',
                  true
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={() => {
                  if (isBlank(this.state.selectedFAQ.Question) || isOnlyWhitespace(this.state.selectedFAQ.Question)) {
                    return sweetalert.fire("Attention", "Question cannot be empty.", "error")
                  }
                  if (isBlank(this.state.selectedFAQ.Answer) || isOnlyWhitespace(this.state.selectedFAQ.Answer)) {
                    return sweetalert.fire("Attention", "Answer cannot be empty.", "error")
                  }
                  this.props.onSubmit(this.state.selectedFAQ);
                }}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
